import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { priorityBadgeClass } from "../lib/badges";

const STATUSES = ["open", "in_progress", "resolved"];

export default function Complaints() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    property_id: "", tenant_id: "", title: "", description: "", priority: "medium",
  });

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/complaints"), api.get("/properties"), api.get("/tenants")]);
    setItems(a.data); setProps(b.data); setTenants(c.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.tenant_id) delete payload.tenant_id;
      await api.post("/complaints", payload);
      toast.success("Ticket created");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    await api.patch(`/complaints/${id}?status=${status}`); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete ticket?")) return;
    await api.delete(`/complaints/${id}`); load();
  };

  const propName = (id) => props.find((p) => p.id === id)?.name || "—";
  const tenantName = (id) => tenants.find((t) => t.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="complaints-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Support</div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter">Complaints & tickets</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-complaint-btn"
                disabled={props.length === 0}
                className="bg-blue-600 text-white px-5 py-3 hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50">
          <Plus className="h-4 w-4" /> New ticket
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 grid md:grid-cols-3 gap-4" data-testid="complaint-form">
          <select required className="border border-slate-300 px-4 py-2.5" value={form.property_id}
                  onChange={(e) => setForm({ ...form, property_id: e.target.value })}>
            <option value="">Select property…</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className="border border-slate-300 px-4 py-2.5" value={form.tenant_id}
                  onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}>
            <option value="">Tenant (optional)</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select className="border border-slate-300 px-4 py-2.5" value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {["low", "medium", "high"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input required placeholder="Title" data-testid="comp-title"
                 className="border border-slate-300 px-4 py-2.5 md:col-span-3"
                 value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea required placeholder="Description" data-testid="comp-desc" rows={3}
                    className="border border-slate-300 px-4 py-2.5 md:col-span-3"
                    value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="md:col-span-3 flex gap-3">
            <button data-testid="comp-save" className="bg-slate-900 text-white px-6 py-2.5">Save</button>
            <button type="button" onClick={() => setOpen(false)} className="bg-slate-100 px-6 py-2.5">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200">
        {items.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="complaints-empty">No tickets yet.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-6 py-3">Title</th><th className="px-6 py-3">Property / Tenant</th>
                <th className="px-6 py-3">Priority</th><th className="px-6 py-3">Status</th><th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-4 align-top">
                    <div className="font-medium">{c.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{c.description}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 align-top">
                    {propName(c.property_id)}{c.tenant_id ? ` · ${tenantName(c.tenant_id)}` : ""}
                  </td>
                  <td className="px-6 py-4 align-top">
                    <span className={priorityBadgeClass(c.priority)}>{c.priority}</span>
                  </td>
                  <td className="px-6 py-4 align-top">
                    <select value={c.status} onChange={(e) => setStatus(c.id, e.target.value)}
                            data-testid={`comp-status-${c.id}`}
                            className="border border-slate-300 px-2 py-1 text-xs uppercase tracking-wider">
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4 text-right align-top">
                    <button onClick={() => remove(c.id)} className="text-red-600 hover:bg-red-50 p-2"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
